import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { listDokumen,fileBaseURL } from '../../Api';
import SkeletonLoader from '../../components/SkeletonLoader'; 
import PullToRefresh from 'react-pull-to-refresh';

// Function to parse Indonesian date format (dd-MMM-yyyy) into JavaScript Date
const parseDateWithIndonesianMonth = (dateString) => {
  const months = {
    Jan: "01",
    Feb: "02",
    Mar: "03",
    Apr: "04",
    Mei: "05",
    Jun: "06",
    Jul: "07",
    Agu: "08",
    Sep: "09",
    Okt: "10",
    Nov: "11",
    Des: "12",
  };

  const [day, month, year] = dateString.split("-");
  const normalizedMonth = months[month];
  if (!normalizedMonth) return null; // Invalid month

  return new Date(`${year}-${normalizedMonth}-${day}`);
};

function Dokumen({ dateRange }) {
  const [documents, setDocuments] = useState(null);
  const [filteredDocuments, setFilteredDocuments] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const fetchOnceRef = useRef(false);
  const navigate = useNavigate(); // For navigation without URL exposure

  const fetchDocuments = async () => {
    setLoading(true); 
    try {
      const data = await listDokumen();
      setDocuments(data);
    } catch (err) {
      setError('Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  const filterDocuments = useCallback(() => {
    if (!documents) return;
  
    if (!dateRange) {
      setFilteredDocuments(documents);
      return;
    }
  
    const today = new Date();
    const pastDate = new Date();
    pastDate.setDate(today.getDate() - dateRange);
  
    const filtered = {};
  
    Object.keys(documents).forEach((monthYear) => {
      const filteredItems = documents[monthYear].filter((item) => {
        const itemDate = parseDateWithIndonesianMonth(item.tgl_dokumen);
  
        return (
          itemDate &&
          itemDate >= pastDate &&
          itemDate <= today
        );
      });
  
      if (filteredItems.length > 0) {
        filtered[monthYear] = filteredItems;
      }
    });
  
    setFilteredDocuments(filtered);
  }, [documents, dateRange]);

  useEffect(() => {
    if (fetchOnceRef.current) return;
    fetchOnceRef.current = true;
    fetchDocuments();
  }, []);

  useEffect(() => {
    filterDocuments();
  }, [filterDocuments]);

  const handleDocumentClick = (doc) => {
    if (typeof doc === 'undefined') return;
  
    const fileUrl = `${fileBaseURL}${doc.nama_dokumen}`;
    
    if (doc.nama_dokumen.endsWith('.pdfx')) {
      // Navigate to PDF viewer without exposing the file in URL
      navigate('/pdf-viewer', { state: { file: fileUrl } });
    } else {
      const link = window.document.createElement('a');
      link.href = fileUrl;
      link.download = doc.nama_dokumen;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      window.document.body.appendChild(link); // Now this will work
      link.click();
      window.document.body.removeChild(link);
    }
  };
  

  const handleRefresh = async () => {
    await fetchDocuments();
  };

  return (
    <PullToRefresh onRefresh={handleRefresh}>
      <div className="dokumenpage">
        {loading ? (
          <div>
            <SkeletonLoader count={3} height={50} />
            <SkeletonLoader count={5} height={30} />
          </div>
        ) : error ? (
          <p>{error}</p>
        ) : filteredDocuments && Object.keys(filteredDocuments).length === 0 ? (
          <p className="infotext">Anda Belum memiliki Dokumen</p> // Message when no data is available
        ) : (
          filteredDocuments &&
          Object.keys(filteredDocuments).map((monthYear) => (
            <div key={monthYear} className="dokumen-group">
              <h3 className="month-header">{monthYear}</h3>
              <div className="item-list">
                {filteredDocuments[monthYear].map((item, index) => (
                  <div key={`${item.id}-${index}`} className="item">
                    <div className="item-date">
                      <span className="item-day">
                        {item.tgl_dokumen.split('-')[0]}
                      </span>
                      <span className="item-month">
                        {item.tgl_dokumen.split('-')[1]}
                      </span>
                      <span className="item-year">
                        {item.tgl_dokumen.split('-')[2]}
                      </span>
                    </div>
                    <div className="item-content">
                      <h3
                        className="item-title"
                        onClick={() => handleDocumentClick(item)}
                        style={{ cursor: 'pointer' }}
                      >
                        {item.nama_dokumen}
                      </h3>
                      <p className="item-description">
                        {item.size}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </PullToRefresh>
  );
}

export default Dokumen;
